/**
 * Profile self-service hooks - ``GET /api/v1/me`` + ``PUT /api/v1/me``.
 *
 * Added for the Include Ticket History toggle (2026-07-14): the
 * SettingsPage needs the CURRENT server value of
 * ``email_include_history``, which the auth store's minimal ``User``
 * snapshot does not carry. Server response is written back into the
 * query cache on save so the toggle never flickers stale.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import type { UserResponse } from "@/types/api";

export const PROFILE_QUERY_KEY = ["me", "profile"] as const;

/** Mirrors backend ``UserUpdateRequest`` - all fields optional. */
export interface ProfileUpdateBody {
  full_name?: string;
  avatar_url?: string;
  email_include_history?: boolean;
}

export function useProfile(): UseQueryResult<UserResponse> {
  return useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: async () => (await api.get<UserResponse>("/me")).data,
  });
}

export function useUpdateProfile(): UseMutationResult<
  UserResponse,
  unknown,
  ProfileUpdateBody
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ProfileUpdateBody) =>
      (await api.put<UserResponse>("/me", body)).data,
    onSuccess: (data) => {
      queryClient.setQueryData(PROFILE_QUERY_KEY, data);
    },
  });
}
